import { memo, useMemo, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { recordFilterRoute } from '@/hooks/useFilterGoPage';
import classnames from 'classnames';

import { useRecoilValue } from 'recoil';
import { globalState } from '@/store/global';
import { userState } from '@/store/user';
import { useI18n } from '@/store/i18n';
import locales from './locales';

import { filterRoutesByFunctionList, formatRoutes, getBreadcrumbRoutes } from '@/utils/router';
import { getPrimaryMenuKey } from '@/hooks/useFilterGoPage';
import { BreadcrumbType } from '@/@types/router';
import useTabNavSync from '@/hooks/useTabNavSync';

import Permission from '@/components/Permission';
import LeftSider from './components/LeftSider';
import RightTop from './components/RightTop';
import RightFooter from './components/RightFooter';
import layoutRotes from './routes';
import PageLoading from '@/components/PageLoading';
import useTitle from '@/hooks/useTitle';
import CachedPage from './components/CachedPage';

import './css/index.less';


export default memo(() => {
  const location = useLocation();
  const t = useRecoilValue(useI18n(locales));
  const global = useRecoilValue(globalState);
  const user = useRecoilValue(userState);
  const routerPathKeyRouter = useMemo(() => formatRoutes(layoutRotes), []);

  // 未绑定角色，或角色中尚无当前系统权限的历史账号，保留全量菜单用于权限初始化。
  const menuRouterPathKey = useMemo(() => {
    const currentPermissionPaths = new Set(
      layoutRotes
        .filter((route) => route.children?.length)
        .map((route) => route.path),
    );
    const hasCurrentPermission = user.adminUser?.functionList?.some((permission) =>
      currentPermissionPaths.has(permission.path),
    );
    if (
      user.adminUser?.admin
      || !user.adminUser?.roleIdList?.length
      || !hasCurrentPermission
    ) {
      return routerPathKeyRouter;
    }
    const filteredRoutes = filterRoutesByFunctionList(
      layoutRotes,
      user.adminUser?.functionList,
    );
    const homeRoute = layoutRotes.find((route) => route.path === '/home');
    return formatRoutes(homeRoute ? [homeRoute, ...filteredRoutes] : filteredRoutes);
  }, [
    routerPathKeyRouter,
    user.adminUser?.admin,
    user.adminUser?.roleIdList,
    user.adminUser?.functionList,
  ]);
  const tabNavList = useMemo(() => global.headTabNavList, [global]);
  const routeItem = useMemo(() => routerPathKeyRouter.pathKeyRouter[location.pathname], [location]);

  // 维护 headTabNavList：将当前路由加入标签页列表，确保页面内容能正常渲染
  // （与 tab 导航 UI 是否展示解耦，即使去掉 RightTabNav 也不影响页面显示）
  useTabNavSync(routerPathKeyRouter.pathKeyRouter);

  const breadCrumbs = useMemo(() => {
    // 基于 PARENT_SECONDARY_MAP 判断一级/二级关系
    const primaryKey = getPrimaryMenuKey(location.pathname);
    if (primaryKey) {
      const primaryRoute = routerPathKeyRouter.pathKeyRouter[primaryKey];
      const currentRoute = routerPathKeyRouter.pathKeyRouter[location.pathname];
      if (primaryRoute && currentRoute) {
        if (primaryKey === location.pathname) {
          // 一级目录：模块名称 / 一级目录
          const modulePath = primaryRoute.meta?.parentPath?.[0];
          const moduleRoute = modulePath ? routerPathKeyRouter.pathKeyRouter[modulePath] : undefined;
          const crumbs: BreadcrumbType[] = [];
          if (moduleRoute) {
            crumbs.push({ title: t(moduleRoute.meta?.title || ''), path: moduleRoute.path });
          }
          crumbs.push({ title: t(primaryRoute.meta?.title || ''), path: primaryRoute.path });
          return crumbs;
        }
        // 二级目录：模块名称 / 一级目录 / 二级目录
        const modulePath = primaryRoute.meta?.parentPath?.[0];
        const moduleRoute = modulePath ? routerPathKeyRouter.pathKeyRouter[modulePath] : undefined;
        const crumbs: BreadcrumbType[] = [];
        if (moduleRoute) {
          crumbs.push({ title: t(moduleRoute.meta?.title || ''), path: moduleRoute.path });
        }
        crumbs.push({ title: t(primaryRoute.meta?.title || ''), path: primaryRoute.path });
        crumbs.push({ title: t(currentRoute.meta?.title || ''), path: currentRoute.path });
        return crumbs;
      }
    }
    return getBreadcrumbRoutes(location.pathname, routerPathKeyRouter.pathKeyRouter).map((item) => ({
      ...item,
      title: t(item.title),
    }));
  }, [location, routerPathKeyRouter, t]);
  useTitle(t(routeItem?.meta?.title || ''));

  // 全局记录路由访问历史，供 useFilterGoPage 判断「来源是否为二级页」
  useEffect(() => {
    recordFilterRoute(location.pathname);
  }, [location.pathname]);



  return (
    <div id='universallayout' className={classnames({ light: global.theme === 'light' })}>
      {global.navMode === 'inline' && (
        <LeftSider
          collapsed={global.collapsed}
          userRoles={user.roles}
          menuData={menuRouterPathKey.router}
          routeItem={routeItem}
          theme={global.theme}
          leftSiderFixed={global.leftSiderFixed}
        />
      )}
      <div id='universallayout-right'>
        <RightTop
          userRoles={user.roles}
          menuData={menuRouterPathKey.router}
          routeItem={routeItem}
          breadCrumbs={breadCrumbs}
        />
        <div id='universallayout-right-main' style={{ position: 'relative' }}>
          {tabNavList.map((tab) => {
            const route = routerPathKeyRouter.pathKeyRouter[tab.location.pathname];
            const Component = route?.component;
            if (!Component) return null;
            return <CachedPage key={tab.location.pathname} savedLocation={tab.location}>
              <Suspense fallback={<PageLoading />}>
                <Permission role={route.meta?.roles}>
                  <Component />
                </Permission>
              </Suspense>
            </CachedPage>;
          })}
          <RightFooter />
        </div>
      </div>
    </div>
  );
});
